import Cookies from 'js-cookie';

import { AuthStatus } from './auth-status';
import { getMe, isInternalEmail } from './get-me';
import { verifyAuthToken } from './verify-auth-token';

/**
 * Reads the `jwt` cookie and classifies it into an `AuthStatus`. Async because
 * the access decision for non-`superadmin` users depends on whether the caller
 * is internal Cliengo staff — and the email is NOT in the JWT, so it's resolved
 * via `GET /users/me`. Does NOT redirect; the `AuthenticationGuard` renders
 * based on the result.
 *
 * Access: `superadmin` (from the JWT) **or** an internal email
 * (`@cliengo`/`@leadaki`). If `/users/me` fails or the email is not internal
 * → `forbidden`.
 */
export async function resolveAuthStatus(): Promise<AuthStatus> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const jwt: string | undefined = Cookies.get('jwt');
  const check = verifyAuthToken(jwt);

  if (check.kind === 'no-jwt') {
    return { kind: 'no-jwt' };
  }

  if (check.kind === 'ok') {
    return {
      kind: 'ok',
      jwt: check.jwt,
      isAdmin: check.isAdmin,
      isSuperAdmin: check.isSuperAdmin,
    };
  }

  // needs-internal-check: resolve the caller's email via /users/me.
  try {
    const me = await getMe(check.jwt);

    if (isInternalEmail(me.email)) {
      return {
        kind: 'ok',
        jwt: check.jwt,
        isAdmin: check.isAdmin,
        isSuperAdmin: check.isSuperAdmin,
        user: { name: me.name, email: me.email },
      };
    }
  } catch {
    // Could not resolve the user → treat as not allowed.
  }

  return { kind: 'forbidden', jwt: check.jwt };
}
